'use client';

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import { analyticsAllowed } from '@/lib/privacyCookie';

type AnyPostHog = {
  init: (key: string, opts: Record<string, unknown>) => void;
  capture?: (event: string, properties?: Record<string, unknown>) => void;
  debug?: () => void;
  opt_out_capturing?: () => void;
  opt_in_capturing?: () => void;
};

// Stable context for the (lazily-loaded) PostHog client. CRITICAL: the provider
// element type below never changes — only its `value` flips null→client. That
// is what keeps the page subtree from REMOUNTING when PostHog finishes loading
// on idle. The previous implementation swapped `<>{children}</>` for
// `<Provider client={client}>{children}</Provider>`, and that element-type
// change unmounted+remounted the whole app (Navbar, the page, the hero),
// restarting the hero's CSS entrance animation — the "double flash". A context
// VALUE change only re-renders consumers; it never remounts children.
const PostHogClientContext = createContext<AnyPostHog | null>(null);

/** Reactive accessor for the PostHog client (null until it loads on idle). */
export const usePostHogClient = () => useContext(PostHogClientContext);

export function PostHogProvider({ children }: { children: ReactNode }) {
  const [client, setClient] = useState<AnyPostHog | null>(null);

  useEffect(() => {
    // The PostHog project key is a PUBLIC, write-only ingestion key — it ships
    // in the client bundle by design (the app exposes the same one). Hardcoded
    // fallback to project 299126's key so the landing always reports to the
    // right project even if the build-time env var is missing/misconfigured;
    // an explicit `NEXT_PUBLIC_POSTHOG_KEY` still wins.
    const posthogKey =
      process.env.NEXT_PUBLIC_POSTHOG_KEY ||
      'phc_ODg5e4avRVFnoTNDqhUwHpiExDbeScNO6DQZK4TMk6z';
    if (!posthogKey) return;

    let cancelled = false;

    const init = async () => {
      // Dynamic import keeps `posthog-js` (~44 KiB) out of the entry bundle —
      // it lands in its own deferred chunk fetched once the main thread is
      // idle. (We no longer import `posthog-js/react`: the client is provided
      // through our own stable context above, so the React tree never changes
      // shape when PostHog loads.)
      const posthogModule = await import('posthog-js');
      if (cancelled) return;

      const posthog = posthogModule.default as AnyPostHog;
      posthog.init(posthogKey, {
        // Route through the managed reverse proxy (matches the app) so ad
        // blockers — which silently drop 10–25% of direct us.i.posthog.com
        // calls — can't kill landing analytics. Hardcoded (NOT from env) so it
        // can't be misconfigured to the blockable direct host.
        api_host: 'https://t.askfulton.com',
        ui_host: 'https://us.posthog.com',
        // Pin the behaviour-versioned defaults rather than inheriting whatever
        // the installed posthog-js happens to fall back to. With this key
        // absent the SDK uses its OLDEST behaviour, so a version bump can
        // quietly move capture_pageview, the external-script injection target,
        // and storage behaviour underneath us. Matched to the app
        // (fulton-app/src/lib/posthogClientOptions.ts) so both surfaces
        // reporting into project 299126 resolve identically. At this value the
        // only deltas that actually reach this init are
        // external_scripts_inject_target ('body' to 'head') and
        // internal_or_test_user_hostname gaining a localhost regex; the rest of
        // the table is either overridden below or gated on a later date.
        defaults: '2026-01-30',
        person_profiles: 'identified_only',
        capture_pageview: false, // We capture pageviews manually in PostHogPageView.
        capture_pageleave: true,
        // Skip loading the PostHog session-recorder (~51 KiB) and surveys
        // (~32 KiB) feature scripts. Re-enable individually if needed.
        disable_session_recording: true,
        disable_surveys: true,
        // Dead-clicks autocapture is enabled on the project remotely, so the
        // SDK tries to lazy-load its script — but our reverse proxy doesn't
        // serve that static asset, so it fails ("[Dead Clicks] failed to load
        // script"). We don't use the feature here; opt out so it never loads.
        capture_dead_clicks: false,
        loaded: (loaded: AnyPostHog) => {
          if (process.env.NODE_ENV === 'development' && loaded.debug) {
            loaded.debug();
          }
        },
      });

      // Park the initialized instance on window so non-React listeners
      // (the document click delegate, IntersectionObserver views) can read
      // the same singleton. The privacy dialog already grabs it this way.
      (window as unknown as { posthog?: typeof posthog }).posthog = posthog;

      // Respect the user's Analytics opt-out. The dialog writes
      // `fulton_analytics_optout=true` when the user turns Analytics off (and
      // the proxy writes it on GPC). PostHog persists this opt-out in its own
      // localStorage entry, so the check only needs to fire once per page
      // load — subsequent events stay suppressed.
      //
      // `analyticsAllowed()` rather than the raw cookie read: a first-time GPC
      // visitor served a cached page never hit the proxy, so the cookie may
      // not exist yet even though the browser is signalling opt-out.
      if (!analyticsAllowed() && posthog.opt_out_capturing) {
        posthog.opt_out_capturing();
      }

      setClient(posthog);
    };

    // Defer load+init until the browser is idle so analytics doesn't
    // compete with hero paint / hydration. Fallback: short timeout for
    // browsers without requestIdleCallback.
    const ric = (window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
    }).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout?: number }) => number)
      | undefined;
    const handle = ric
      ? ric(() => { void init(); }, { timeout: 2000 })
      : window.setTimeout(() => { void init(); }, 1500);

    return () => {
      cancelled = true;
      const cancelRic = (window as Window & {
        cancelIdleCallback?: (handle: number) => void;
      }).cancelIdleCallback;
      if (ric && typeof cancelRic === "function") {
        cancelRic(handle);
      } else {
        window.clearTimeout(handle as number);
      }
    };
  }, []);

  // ALWAYS the same element type — only `value` changes (null → client) when
  // PostHog loads. Children are never remounted, so the hero entrance plays
  // exactly once. Consumers (PostHogPageView via usePostHogClient) re-render
  // reactively when the client becomes available.
  return (
    <PostHogClientContext.Provider value={client}>
      {children}
    </PostHogClientContext.Provider>
  );
}
